'use client'

import { Navigate, RouteLink, useParams } from '@/lib/router'
import { SiteShell } from '@/components/site/site-shell'
import { PageIntro } from '@/components/site/page-intro'
import { PageCrumbs } from '@/components/site/page-crumbs'
import { MagneticLink, siteButtonClass } from '@/components/site/magnetic-button'
import { getCaseStudyBySlug, getRelatedCaseStudies } from '@/content/work'
import { getCaseStudyHero } from '@/lib/portfolioDisplay'
import { BreadcrumbJsonLd } from '@/seo/BreadcrumbJsonLd'

export default function CaseStudyPage() {
  const { slug } = useParams<{ slug: string }>()
  const study = getCaseStudyBySlug(slug)

  if (!study) {
    return <Navigate to="/portfolio" replace />
  }

  const hero = getCaseStudyHero(study.portfolioName)
  const related = getRelatedCaseStudies(study.slug)
  const crumbs = [
    { name: 'Home', path: '/' },
    { name: 'Work', path: '/portfolio' },
    { name: study.client, path: study.path },
  ]

  return (
    <SiteShell>
      <BreadcrumbJsonLd items={crumbs} />
      <div className="w-full bg-background px-6 pb-20 pt-24 md:pb-32 lg:px-12">
        <div className="mx-auto max-w-5xl">
          <div className="max-w-3xl">
            <PageCrumbs items={crumbs} />
            <PageIntro
              eyebrow={`${study.lab ? 'Lab · ' : ''}${study.location} · ${study.year}`}
              title={study.title}
              description={study.excerpt}
            />
          </div>

          {hero && (
            <figure className="mb-16 overflow-hidden border border-border bg-muted/30 md:mb-20">
              <img
                src={hero}
                alt={`${study.client} - project screenshot`}
                className="h-auto w-full object-contain object-top"
              />
              <figcaption className="border-t border-border px-4 py-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">
                {study.lab ? 'Lab build' : 'Client launch'} · {study.client}
              </figcaption>
            </figure>
          )}

          <div className="max-w-3xl">
            <dl className="mb-16 grid gap-6 border-y border-border py-8 sm:grid-cols-2">
              <div>
                <dt className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  {study.lab ? 'Product' : 'Client'}
                </dt>
                <dd className="mt-2 font-serif text-xl">{study.client}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Industry</dt>
                <dd className="mt-2 font-serif text-xl">{study.industry}</dd>
              </div>
            </dl>

            <section className="mb-12">
              <h2 className="mb-4 font-serif text-2xl text-foreground">The brief</h2>
              <p className="text-lg leading-relaxed text-muted-foreground">{study.challenge}</p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 font-serif text-2xl text-foreground">What we did</h2>
              <ul className="space-y-3 text-lg leading-relaxed text-muted-foreground">
                {study.approach.map((item) => (
                  <li key={item} className="flex gap-3">
                    <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="mb-16">
              <h2 className="mb-4 font-serif text-2xl text-foreground">Status</h2>
              <p className="text-lg leading-relaxed text-muted-foreground">{study.outcome}</p>
            </section>

            <div className="flex flex-wrap gap-4">
              <a
                href={study.liveUrl}
                target="_blank"
                rel="noreferrer"
                className={siteButtonClass.primary}
              >
                {study.lab ? 'Open the prototype' : 'Visit live site'}
              </a>
              <MagneticLink href="/contact" variant="outline">
                Start a similar project
              </MagneticLink>
            </div>
          </div>

          {related.length > 0 && (
            <section className="mt-24 border-t border-border pt-16">
              <p className="mb-8 text-xs uppercase tracking-[0.3em] text-accent">More work</p>
              <ul className="grid gap-8 sm:grid-cols-3">
                {related.map((item) => (
                  <li key={item.slug}>
                    <RouteLink to={item.path} className="group block">
                      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                        {item.lab ? 'Lab' : 'Client'} · {item.year}
                      </p>
                      <h3 className="mt-2 font-serif text-xl group-hover:text-accent">{item.client}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.excerpt}</p>
                    </RouteLink>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </SiteShell>
  )
}
